import { useState, useEffect, useCallback } from 'react';
import { Wrench, X, ShieldAlert, Check, Copy, ChevronDown } from 'lucide-react';
import { ScrollReveal } from '@/components/effects/ScrollReveal';

const PRIORITY_BADGE = {
  critical: { bg: 'var(--danger)', fg: 'var(--white)', label: 'CRITICAL' },
  high:     { bg: 'var(--warn)',   fg: 'var(--bg)',    label: 'HIGH' },
  medium:   { bg: 'rgba(139,92,246,0.2)', fg: 'var(--a-bright)', label: 'MEDIUM' },
};

export function PriorityBadge({ priority }) {
  const cfg = PRIORITY_BADGE[priority] || PRIORITY_BADGE.medium;
  return (
    <span
      className="font-mono inline-flex items-center"
      style={{
        fontSize: '0.6rem',
        letterSpacing: '0.1em',
        fontWeight: 700,
        padding: '0.15rem 0.45rem',
        borderRadius: 4,
        background: cfg.bg,
        color: cfg.fg,
      }}
    >
      {cfg.label}
    </span>
  );
}

function DismissButton({ onDismiss }) {
  if (!onDismiss) return null;
  return (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss recommendation"
      data-testid="recommendation-dismiss"
      className="absolute top-2 right-2 flex items-center justify-center transition-colors duration-150"
      style={{
        width: 22,
        height: 22,
        borderRadius: 4,
        background: 'transparent',
        border: '1px solid transparent',
        color: 'var(--ts)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--b2)'; e.currentTarget.style.color = 'var(--white)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--ts)'; }}
    >
      <X size={13} />
    </button>
  );
}

export function AcodiaRecCard({ rec, onDismiss }) {
  return (
    <div
      data-testid={`scanner-rec-acodia-${rec.priority}`}
      className="rounded-[10px] relative"
      style={{
        background: 'rgba(10,10,18,0.88)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--b1)',
        padding: '0.875rem 1rem',
      }}
    >
      <DismissButton onDismiss={onDismiss} />
      <div className="flex items-start gap-3 mb-2" style={{ paddingRight: onDismiss ? 24 : 0 }}>
        <div className="pt-0.5">
          <PriorityBadge priority={rec.priority} />
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className="font-body"
            style={{ fontWeight: 600, color: 'var(--white)', fontSize: '0.9rem', lineHeight: 1.3 }}
          >
            {rec.title}
          </h4>
          <p
            className="font-body mt-1"
            style={{
              color: 'var(--tm)',
              fontSize: '0.82rem',
              lineHeight: 1.55,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {rec.description}
          </p>
        </div>
      </div>
      <a
        href={rec.cta_url}
        className="font-body inline-flex items-center mt-2 transition-colors duration-150"
        style={{
          fontSize: '0.78rem',
          fontWeight: 500,
          color: 'var(--a)',
          background: 'transparent',
          border: '1px solid var(--b2)',
          borderRadius: 6,
          padding: '0.35rem 0.7rem',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--a-border)'; e.currentTarget.style.color = 'var(--a-bright)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b2)'; e.currentTarget.style.color = 'var(--a)'; }}
      >
        {rec.cta_text} →
      </a>
    </div>
  );
}

export function SelfFixCard({ rec, onDismiss }) {
  return (
    <div
      data-testid={`scanner-rec-selffix-${rec.priority}`}
      className="rounded-[10px] relative"
      style={{
        background: 'rgba(10,10,18,0.88)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--b1)',
        borderLeft: '3px solid var(--ok)',
        padding: '0.875rem 1rem',
      }}
    >
      <DismissButton onDismiss={onDismiss} />
      <div className="flex items-start gap-3" style={{ paddingRight: onDismiss ? 24 : 0 }}>
        <Wrench size={16} style={{ color: 'var(--ok)', flexShrink: 0, marginTop: 3 }} />
        <div className="flex-1 min-w-0">
          <h4
            className="font-body"
            style={{ fontWeight: 600, color: 'var(--ok)', fontSize: '0.9rem', lineHeight: 1.3 }}
          >
            {rec.title}
          </h4>
          {rec.description && (
            <p
              className="font-body mt-1"
              style={{ color: 'var(--tm)', fontSize: '0.8rem', lineHeight: 1.55 }}
            >
              {rec.description}
            </p>
          )}
          {rec.self_fix_instruction && (
            <>
              <p
                className="font-body mt-2.5"
                style={{ fontSize: '0.75rem', color: 'var(--ts)' }}
              >
                How to fix this yourself:
              </p>
              <p
                className="font-mono mt-1"
                style={{
                  color: 'var(--tm)',
                  fontSize: '0.78rem',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {rec.self_fix_instruction}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Security headers card ───────────────────────────────────────────
   Rendered for recommendations with type === 'security_headers'. Shows the
   customer exactly which headers are present/missing, what each one does,
   copy-paste fix instructions per web server, and a secondary Acodia Defend
   CTA. Falls back to a basic card if the rec predates the detailed fields. */

// Canonical header name → one-line, plain-English description of the threat
// it prevents. Keyed by canonical case; a lowercase lookup is also tried so
// legacy lowercase data still resolves.
const HEADER_META = {
  'Content-Security-Policy': 'Prevents XSS by controlling which scripts and resources are allowed to load',
  'Strict-Transport-Security': 'Forces browsers to always connect over HTTPS',
  'X-Frame-Options': 'Prevents clickjacking by blocking your site from being embedded in an iframe',
  'X-Content-Type-Options': 'Stops browsers from guessing (sniffing) file types',
  'Referrer-Policy': 'Controls how much referrer information is shared with third-party sites',
  'Permissions-Policy': 'Restricts access to browser features like camera, microphone and location',
};

const _HEADER_META_LC = Object.fromEntries(
  Object.entries(HEADER_META).map(([k, v]) => [k.toLowerCase(), v]),
);

function headerDescription(name) {
  if (!name) return '';
  return HEADER_META[name] || _HEADER_META_LC[name.toLowerCase()] || '';
}

const FIX_TABS = [
  { key: 'nginx', label: 'Nginx' },
  { key: 'apache', label: 'Apache' },
  { key: 'cloudflare', label: 'Cloudflare' },
  { key: 'other', label: 'Other web servers' },
];

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        // Fallback for non-secure contexts where the Clipboard API is absent.
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — leave the snippet for manual copy */
    }
  }, [code]);

  return (
    <div className="relative" style={{ marginTop: '0.6rem' }}>
      <button
        type="button"
        onClick={onCopy}
        data-testid="header-fix-copy"
        aria-label="Copy config snippet"
        className="absolute flex items-center gap-1 transition-colors duration-150"
        style={{
          top: 8,
          right: 8,
          fontSize: '0.68rem',
          fontWeight: 600,
          padding: '0.2rem 0.45rem',
          borderRadius: 5,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--b2)',
          color: copied ? 'var(--ok)' : 'var(--ts)',
          cursor: 'pointer',
        }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre
        className="font-mono"
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--b1)',
          borderRadius: 8,
          padding: '0.75rem 0.85rem',
          paddingRight: '4.2rem',
          fontSize: '0.72rem',
          lineHeight: 1.6,
          color: 'var(--tm)',
          overflowX: 'auto',
          whiteSpace: 'pre',
          margin: 0,
        }}
      >
        <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{code}</code>
      </pre>
    </div>
  );
}

export function SecurityHeadersCard({ rec, onDismiss }) {
  const missing = Array.isArray(rec?.missing_headers) ? rec.missing_headers : [];
  const present = Array.isArray(rec?.present_headers) ? rec.present_headers : [];
  const fix = rec?.fix_instructions || {};
  const availableTabs = FIX_TABS.filter((t) => fix[t.key]);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(availableTabs[0]?.key || 'nginx');

  // Backward compatibility: a rec from before the detailed fields existed has
  // no missing_headers — render the original simple self-fix card instead.
  if (missing.length === 0) {
    return <SelfFixCard rec={rec} onDismiss={onDismiss} />;
  }

  return (
    <div
      data-testid="scanner-rec-security-headers"
      className="rounded-[10px] relative"
      style={{
        background: 'rgba(10,10,18,0.88)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--b1)',
        borderLeft: '3px solid var(--warn)',
        padding: '0.875rem 1rem',
      }}
    >
      <DismissButton onDismiss={onDismiss} />
      <div className="flex items-start gap-3 mb-1" style={{ paddingRight: onDismiss ? 24 : 0 }}>
        <ShieldAlert size={16} style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 3 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <PriorityBadge priority={rec.priority} />
            <h4
              className="font-body"
              style={{ fontWeight: 600, color: 'var(--white)', fontSize: '0.9rem', lineHeight: 1.3 }}
            >
              {rec.title}
            </h4>
          </div>
          <p
            className="font-body mt-1"
            style={{ color: 'var(--tm)', fontSize: '0.82rem', lineHeight: 1.55 }}
          >
            {rec.description}
          </p>
        </div>
      </div>

      {/* Present headers — positive reinforcement */}
      {present.length > 0 && (
        <div style={{ marginTop: '0.85rem' }}>
          <p
            className="font-mono uppercase"
            style={{ fontSize: '0.62rem', letterSpacing: '0.12em', color: 'var(--ts)', marginBottom: '0.4rem' }}
          >
            Present headers
          </p>
          <div className="space-y-1">
            {present.map((h) => (
              <div key={`p-${h}`} className="flex items-start gap-2">
                <Check size={13} style={{ color: 'var(--ok)', flexShrink: 0, marginTop: 3 }} />
                <p className="font-body" style={{ fontSize: '0.76rem', lineHeight: 1.5, color: 'var(--tm)' }}>
                  <span style={{ color: 'var(--ok)', fontWeight: 600 }}>{h}</span>
                  {headerDescription(h) ? ` — ${headerDescription(h)}` : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing headers */}
      <div style={{ marginTop: '0.85rem' }}>
        <p
          className="font-mono uppercase"
          style={{ fontSize: '0.62rem', letterSpacing: '0.12em', color: 'var(--ts)', marginBottom: '0.4rem' }}
        >
          Missing headers
        </p>
        <div className="space-y-1">
          {missing.map((h) => (
            <div key={`m-${h}`} className="flex items-start gap-2">
              <X size={13} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 3 }} />
              <p className="font-body" style={{ fontSize: '0.76rem', lineHeight: 1.5, color: 'var(--tm)' }}>
                <span style={{ color: 'var(--white)', fontWeight: 600 }}>{h}</span>
                {headerDescription(h) ? ` — ${headerDescription(h)}` : ''}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* How to fix this yourself — expandable, tabbed */}
      {availableTabs.length > 0 && (
        <div style={{ marginTop: '0.95rem' }}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="header-fix-toggle"
            aria-expanded={open}
            className="font-body inline-flex items-center gap-1.5 transition-colors duration-150"
            style={{
              fontSize: '0.78rem',
              fontWeight: 600,
              color: 'var(--a)',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <Wrench size={13} />
            How to fix this yourself
            <ChevronDown
              size={14}
              style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
            />
          </button>

          {open && (
            <div style={{ marginTop: '0.65rem' }}>
              <div className="flex" style={{ gap: '0.35rem', flexWrap: 'wrap' }}>
                {availableTabs.map((t) => {
                  const active = t.key === tab;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setTab(t.key)}
                      data-testid={`header-fix-tab-${t.key}`}
                      className="font-body transition-colors duration-150"
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        padding: '0.3rem 0.65rem',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: active ? 'var(--a)' : 'transparent',
                        color: active ? 'var(--white)' : 'var(--ts)',
                        border: `1px solid ${active ? 'var(--a)' : 'var(--b2)'}`,
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <CodeBlock key={tab} code={fix[tab] || ''} />

              <p className="font-body" style={{ fontSize: '0.72rem', color: 'var(--ts)', lineHeight: 1.5, marginTop: '0.55rem' }}>
                Add these to your server config and restart. After deploying, run a fresh scan to confirm.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Secondary CTA — Acodia Defend */}
      {rec.cta_url && (
        <a
          href={rec.cta_url}
          data-testid="header-fix-defend-cta"
          className="font-body inline-flex items-center transition-colors duration-150"
          style={{ marginTop: '0.9rem', fontSize: '0.74rem', fontWeight: 500, color: 'var(--ts)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--a-bright)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ts)'; }}
        >
          {rec.cta_text || 'Prefer we handle it? Get security headers configured by Acodia Defend'} →
        </a>
      )}
    </div>
  );
}

/* ── Dismiss persistence ─────────────────────────────────────────────
   localStorage key: "acodia_dismissed_recs"
   Shape: { [recId]: expiryMs }  — entries older than now are expired.
   Expiry window: 30 days.
*/

const DISMISS_KEY = 'acodia_dismissed_recs';
const DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function recId(rec) {
  if (!rec) return '';
  if (rec.id) return String(rec.id);
  return `${rec.priority || 'medium'}::${rec.title || ''}`;
}

function readDismissed() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const now = Date.now();
    const clean = {};
    for (const [id, expiry] of Object.entries(parsed)) {
      if (typeof expiry === 'number' && expiry > now) clean[id] = expiry;
    }
    return clean;
  } catch {
    return {};
  }
}

function writeDismissed(map) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {
    /* storage full or blocked — ignore */
  }
}

export function useDismissedRecs() {
  const [dismissed, setDismissed] = useState(() => readDismissed());

  const dismiss = useCallback((id) => {
    if (!id) return;
    setDismissed((prev) => {
      const next = { ...prev, [id]: Date.now() + DISMISS_WINDOW_MS };
      writeDismissed(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === DISMISS_KEY) setDismissed(readDismissed());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isDismissed = useCallback((id) => Boolean(dismissed[id]), [dismissed]);
  return { isDismissed, dismiss };
}

/* ── Main panel (scanner variant) ─────────────────────────────────── */

export function RecommendationsPanel({ recommendations }) {
  const recs = Array.isArray(recommendations) ? recommendations : [];
  if (recs.length === 0) return null;

  const headerRecs = recs.filter((r) => r && r.type === 'security_headers');
  const acodiaRecs = recs.filter((r) => r && r.acodia_service && r.type !== 'security_headers');
  const selfFixRecs = recs.filter((r) => r && !r.acodia_service && r.self_fix && r.type !== 'security_headers');

  if (acodiaRecs.length === 0 && selfFixRecs.length === 0 && headerRecs.length === 0) return null;

  const showAcodiaSection = acodiaRecs.length > 0;
  const hasSelfFix = selfFixRecs.length > 0 || headerRecs.length > 0;
  const sectionTitle = showAcodiaSection ? 'Where Acodia Can Help' : 'What to fix next';
  const showGroupLabels = showAcodiaSection && hasSelfFix;

  return (
    <ScrollReveal delay={0.18}>
      <div
        data-testid="scanner-recommendations"
        className="rounded-lg p-6 sm:p-8"
        style={{
          background: 'rgba(10,10,18,0.88)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--b1)',
        }}
      >
        <h3 className="font-display text-lg font-bold mb-1" style={{ color: 'var(--white)' }}>
          {sectionTitle}
        </h3>
        {showAcodiaSection && (
          <p className="font-body mb-5" style={{ fontSize: '0.85rem', color: 'var(--ts)', lineHeight: 1.5 }}>
            Based on your scan results our team can help with the following.
          </p>
        )}

        {showAcodiaSection && (
          <>
            {showGroupLabels && (
              <p
                className="font-body uppercase mb-3"
                style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: 'var(--ts)' }}
              >
                Get expert help with these
              </p>
            )}
            <div className="space-y-2.5">
              {acodiaRecs.map((rec, i) => (
                <AcodiaRecCard key={`ag-${i}-${rec.title}`} rec={rec} />
              ))}
            </div>
          </>
        )}

        {hasSelfFix && (
          <>
            {showGroupLabels && (
              <p
                className="font-body uppercase mt-6 mb-3"
                style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: 'var(--ts)' }}
              >
                Fix these yourself
              </p>
            )}
            <div className={`space-y-2.5 ${showAcodiaSection && !showGroupLabels ? 'mt-3' : ''}`}>
              {headerRecs.map((rec, i) => (
                <SecurityHeadersCard key={`sh-${i}-${rec.title}`} rec={rec} />
              ))}
              {selfFixRecs.map((rec, i) => (
                <SelfFixCard key={`sf-${i}-${rec.title}`} rec={rec} />
              ))}
            </div>
          </>
        )}
      </div>
    </ScrollReveal>
  );
}

/* ── Dashboard variant — compact with dismiss + "all reviewed" state ── */

export function DashboardRecommendationsPanel({ recommendations, testId = 'dashboard-recommendations' }) {
  const { isDismissed, dismiss } = useDismissedRecs();
  const recs = Array.isArray(recommendations) ? recommendations : [];
  if (recs.length === 0) return null;

  const totalCount = recs.length;
  const visible = recs.filter((r) => !isDismissed(recId(r)));
  const headerRecs = visible.filter((r) => r && r.type === 'security_headers');
  const acodiaRecs = visible.filter((r) => r && r.acodia_service && r.type !== 'security_headers');
  const selfFixRecs = visible.filter((r) => r && !r.acodia_service && r.self_fix && r.type !== 'security_headers');

  const allDismissed = visible.length === 0;

  return (
    <div
      data-testid={testId}
      className="rounded-lg p-5 sm:p-6"
      style={{
        background: 'rgba(10,10,18,0.88)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--b1)',
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-display text-base font-bold" style={{ color: 'var(--white)' }}>
          Recommended Actions
        </h3>
        <span
          data-testid="dashboard-recommendations-count"
          className="font-mono inline-flex items-center justify-center"
          style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            minWidth: 22,
            height: 20,
            padding: '0 0.45rem',
            borderRadius: 999,
            background: 'var(--a)',
            color: 'var(--white)',
          }}
        >
          {totalCount}
        </span>
      </div>

      {allDismissed ? (
        <p
          data-testid="dashboard-recommendations-all-reviewed"
          className="font-body"
          style={{ fontSize: '0.875rem', color: 'var(--ok)', fontFamily: 'DM Sans, sans-serif' }}
        >
          All recommendations reviewed — well done.
        </p>
      ) : (
        <div className="space-y-2.5">
          {acodiaRecs.map((rec, i) => {
            const id = recId(rec);
            return (
              <AcodiaRecCard
                key={`d-ag-${i}-${id}`}
                rec={rec}
                onDismiss={() => dismiss(id)}
              />
            );
          })}
          {headerRecs.map((rec, i) => {
            const id = recId(rec);
            return (
              <SecurityHeadersCard
                key={`d-sh-${i}-${id}`}
                rec={rec}
                onDismiss={() => dismiss(id)}
              />
            );
          })}
          {selfFixRecs.map((rec, i) => {
            const id = recId(rec);
            return (
              <SelfFixCard
                key={`d-sf-${i}-${id}`}
                rec={rec}
                onDismiss={() => dismiss(id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
